// docs/21 §7 (docs/182 EP): after the first battle. The field it was fought on — the board's own
// ground kit, hour, weather and season (`encounter_stage`), so the morning after looks like the
// fight did — with everyone who fought standing on it: every player's band in their colour, a
// ranger's beast at their heel, emotes over them, a click for any hero's sheet and, on my own,
// their gear and a waiting level. Over it, in solid panels that read at a glance: the result, the
// story's victory or defeat text, what the settlement paid, the hook, the first named foe and the
// campaign's first quest — then into the World. The named foe's getaway and vow played on the board
// (Opening.tsx `EscapeView`); once seen they are not told again, only what is new: why they could not
// fall (docs/22 O4). After a defeat there was no getaway, so their vow stands here.
import { useEffect } from 'react';
import { Badge, Button, HeroSprite, Stamp } from '../../components';
import { classLook, type HeroLook } from '../../../game/heroKit';
import { useSession } from '../../../core/stores/session';
import { useRoute } from '../../../core/stores/route';
import { useCinematic } from '../../../core/stores/cinematic';
import { leaveEncounter } from '../../../core/encounter/session';
import { identityHex } from '../../../core/net/connection';
import { CLASS_NAMES } from '../../../core/sim/adapt';
import { companionIdOf } from '../../../core/sim/companion';
import { useEncounterStage, useGenesis, useHeroCallings, useMyLevelUpOffers, useNames, useOffers, useSettlement, useWorld, useWorldClock, useWorldHeroes, useWorldMembers, useWorldOpening } from '../../../core/subscriptions/hooks';
import { display, placeOf, useStoryMarks, withHeroes, wonRow, type Genesis } from '../../../core/story/genesis';
import { fieldLine, fieldOf, type EpilogueField } from './epilogueField';
import { EmoteAnchor, PetWiggle } from '../../emote/EmoteBubble';
import { openHeroSheet } from '../../heroes/heroSheetStore';
import { openInventory } from '../../inventory/store';
import { useOverlay } from '../../overlay';
import '../../heroes/heroes.css';
import { ChatDock, playerColour, usePlayerColours } from '../ChatDock';
import { beastOf, type SeatBeast } from '../Lobby';
import { cap } from '../forge';
import { SceneBackdrop } from './Scene';
import './intro.css';
import { useMusicMood } from '../../../core/audio/music';
import { playSfx } from '../../../core/audio';

export interface EpilogueQuest { title: string; asker: string; place: string; pitch: string; kind: string }
/** One hero who fought: `id` opens their sheet (none in the harness); `levelUp` is my offer waiting. */
export interface EpilogueHero { key: string; id?: bigint; look?: HeroLook; name: string; frame?: number; classId: string; level: number; beast?: SeatBeast; levelUp?: { id: bigint; toLevel: number } }
/** One player's band on the field, keeper first. */
export type { EpilogueField };
export interface EpilogueBand { key: string; player: string; colour?: number; you: boolean; keeper?: boolean; heroes: EpilogueHero[] }
/** Heroes stand at the scene's 3× until the field gets crowded, then at 2× (as on the writing screen). */
const CROWD = 8;
const beastLine = (b: SeatBeast) => (b.name === b.kind ? b.kind : `${b.name} the ${b.kind}`);

export function EpilogueView({ g, won, bands, field, worldBiome, xp, gold, quest, escapeSeen = true, worldId, onSheet, onGear, onLevelUp, onContinue }: {
  g: Genesis; won: boolean; bands: EpilogueBand[]; field?: EpilogueField; xp?: number; gold?: number; quest?: EpilogueQuest;
  /** the World clock's biome, for the field's encounter art (docs/186) */ worldBiome?: string;
  /** the getaway and vow already played on the board (Opening.tsx) */ escapeSeen?: boolean;
  /** the table's World, for the chat (the harness has none) */ worldId?: bigint;
  onSheet?: (h: EpilogueHero) => void; onGear?: (h: EpilogueHero) => void; onLevelUp?: (offerId: bigint) => void; onContinue: () => void;
}) {
  useMusicMood(won ? 'triumph' : 'sorrow');
  useEffect(() => { playSfx(won ? 'cine.victory' : 'cine.defeat', { whenUnlocked: true }); }, [won]);   // docs/44
  const heroes = bands.flatMap((b) => b.heroes);
  const names = heroes.map((h) => h.name);
  const q = quest ?? { title: g.campaign.title, asker: g.campaign.asker, place: placeOf(g, g.campaign.place).name, pitch: g.campaign.pitch, kind: g.campaign.kind };
  // the field as the board had it; a World from before encounter_stage falls back on the story's stage
  const f: EpilogueField = field ?? { stage: g.battle.stage, season: g.calendar?.season };
  const levelUps = bands.filter((b) => b.you).flatMap((b) => b.heroes.flatMap((h) => (h.levelUp ? [{ hero: h, ...h.levelUp }] : [])));
  return (
    <main className={`fk-intro fk-epilogue ${worldId !== undefined ? 'fk-epilogue--table' : ''}`} data-testid="epilogue-screen" data-won={won ? 'true' : 'false'}>
      <SceneBackdrop scene={won ? 'Dawn' : 'Night'} stage={f.stage} season={f.season} biome={g.realm.biome} worldBiome={worldBiome} />
      <div className="fk-epilogue__body fk-hit">
        <section className="fk-epilogue__story" aria-label="How the battle ended" data-testid="epilogue-story">
          <span className="fk-intro__eyebrow">{g.battle.title}</span>
          <span className="fk-epilogue__where" data-testid="epilogue-field">{display(placeOf(g, g.battle.place).name)} · {fieldLine(g, f)}</span>
          <Stamp tone={won ? 'victory' : 'defeat'} small>{won ? 'Victory' : 'Defeat'}</Stamp>
          <p className="fk-epilogue__text" data-testid="epilogue-text">{withHeroes(won ? g.battle.victory : g.battle.defeat, names)}</p>
          <div className="fk-epilogue__rewards" data-testid="epilogue-rewards">
            {xp !== undefined && <span className="fk-epilogue__reward"><b>+{xp}</b> XP to every hero</span>}
            {!!gold && <span className="fk-epilogue__reward"><b>{gold}</b> gold each</span>}
            {levelUps.map((l) => (
              <Button key={l.id.toString()} small primary onClick={() => onLevelUp?.(l.id)} data-testid="epilogue-levelup">★ {l.hero.name} reaches level {l.toLevel}</Button>
            ))}
          </div>
          <p className="fk-epilogue__hook" data-testid="epilogue-hook">{withHeroes(g.battle.hook, names)}</p>
        </section>
        <div className="fk-epilogue__col">
          <NamedFoeCard g={g} won={won} names={names} told={won && escapeSeen} />
          <div className="fk-panel fk-panel--ornate fk-epilogue__quest" data-testid="epilogue-quest">
            <span className="fk-intro__card-eyebrow">The campaign begins · {display(g.arc.shape)}</span>
            <h2 className="fk-intro__card-title">{q.title}</h2>
            <p className="fk-epilogue__quest-pitch">{q.pitch}</p>
            <p className="fk-epilogue__quest-asker">{q.asker}, at {display(q.place)}</p>
            <Button primary size="lg" onClick={onContinue} data-testid="epilogue-continue">Enter {display(g.realm.name)}</Button>
          </div>
        </div>
      </div>
      <EpilogueParty bands={bands} onSheet={onSheet} onGear={onGear} />
      {worldId !== undefined && <ChatDock worldId={worldId} variant="dock" placement="corner" className="fk-epilogue__chat" />}
    </main>
  );
}

/** Everyone who fought, standing on the field: each band over a rule in its player's colour. */
function EpilogueParty({ bands, onSheet, onGear }: { bands: EpilogueBand[]; onSheet?: (h: EpilogueHero) => void; onGear?: (h: EpilogueHero) => void }) {
  const count = bands.reduce((n, b) => n + b.heroes.reduce((m, h) => m + (h.beast ? 2 : 1), 0), 0);
  if (count === 0) return null;
  const scale = count > CROWD ? 2 : 3;
  return (
    <div className={`fk-epilogue__field ${count > CROWD * 2 ? 'fk-epilogue__field--crowd' : ''}`} style={{ ['--fk-hero-px' as string]: `${24 * scale}px` }}>
      <ul className="fk-epilogue__ground fk-hit" aria-label="The party" data-testid="epilogue-party">
        {bands.map((b) => (
          <li key={b.key} className={`fk-epilogue__band ${b.you ? 'fk-epilogue__band--me' : ''}`} style={{ ['--fk-seat' as string]: playerColour(b.colour) }} data-testid="epilogue-player" data-name={b.player}>
            <span className="fk-epilogue__heroes">
              {b.heroes.map((h) => (
                <span key={h.key} className="fk-epilogue__figure" data-testid="epilogue-figure" data-hero-name={h.name}>
                  <span className="fk-epilogue__pair">
                    <button type="button" className="fk-epilogue__hero fk-hero-open" onClick={() => onSheet?.(h)} aria-label={`${h.name}: see their sheet`} title={`${h.name} · ${cap(h.classId)} · Lv ${h.level}`} data-testid="epilogue-hero" data-hero-name={h.name}>
                      <HeroSprite heroId={h.id} look={h.id === undefined ? h.look ?? classLook(h.classId) : undefined} frame={h.frame} classId={h.classId} scale={scale} title={h.name} />
                      {h.id !== undefined && <EmoteAnchor heroes={[{ id: h.id, name: h.name, mine: b.you }]} />}
                      {h.levelUp && <span className="fk-epilogue__star" aria-label="a level is waiting" title={`Level ${h.levelUp.toLevel} is waiting`}>★</span>}
                    </button>
                    {h.beast && (
                      <span className="fk-epilogue__beast" data-testid="epilogue-beast" data-beast-name={h.beast.name} data-beast-kind={h.beast.kind}>
                        {h.id !== undefined
                          ? <PetWiggle pet={{ id: BigInt(companionIdOf(Number(h.id))), name: h.beast.name, mine: b.you }}><HeroSprite frame={h.beast.frame} classId="companion" scale={2} title={beastLine(h.beast)} /></PetWiggle>
                          : <HeroSprite frame={h.beast.frame} classId="companion" scale={2} title={beastLine(h.beast)} />}
                      </span>
                    )}
                  </span>
                  <span className="fk-epilogue__tag">
                    <b>{h.name}</b>
                    <i>{h.beast ? `& ${beastLine(h.beast)}` : `${cap(h.classId)} · Lv ${h.level}`}</i>
                  </span>
                  {b.you && h.id !== undefined && onGear && (
                    <button type="button" className="fk-epilogue__gear" onClick={() => onGear(h)} data-testid="epilogue-gear" title={`${h.name}'s gear and pack`}>Gear</button>
                  )}
                </span>
              ))}
            </span>
            <span className="fk-epilogue__player">{b.keeper && <span className="fk-epilogue__crown" title="keeper" aria-label="keeper">♛</span>}{b.player}{b.you ? ' (you)' : ''}</span>
          </li>
        ))}
      </ul>
      <p className="fk-epilogue__hint">Click a hero to see their sheet</p>
    </div>
  );
}

/**
 * The World's first named foe after the opening (docs/22 O4). `told`: their getaway and vow already
 * played on the board, so only their name and why they could not fall are new here.
 */
export function NamedFoeCard({ g, won, names, compact = false, told = false }: { g: Genesis; won: boolean; names: string[]; compact?: boolean; told?: boolean }) {
  const n = g.battle.named_foe;
  return (
    <section className={`fk-named-foe ${compact ? 'fk-named-foe--compact' : ''} ${told ? 'fk-named-foe--told' : ''}`} data-testid="epilogue-named-foe" data-told={told ? 'true' : 'false'} aria-label="Your first named foe">
      <span className="fk-named-foe__eyebrow">{won ? 'Your first named foe' : `${display(g.battle.leader.name)} remembers you`}</span>
      <div className="fk-named-foe__head">
        <span className="fk-intro__seal fk-intro__seal--foe" aria-hidden="true"><b>{g.battle.leader.name.replace(/^the\s+/i, '').charAt(0).toUpperCase()}</b></span>
        <span className="fk-named-foe__who"><b data-testid="epilogue-named-foe-name">{display(g.battle.leader.name)}</b><i>{g.battle.leader.title}</i></span>
        {won && <Badge tone="red">got away</Badge>}
      </div>
      {won && !told && <p className="fk-named-foe__getaway">{withHeroes(n.getaway, names)}</p>}
      {!told && <q className="fk-named-foe__vow" data-testid="epilogue-vow">{withHeroes(n.vow, names)}</q>}
      {won && <p className="fk-named-foe__armor" data-testid="epilogue-armor"><span>Why they could not fall</span>{withHeroes(n.armor, names)}</p>}
      {won && <p className="fk-named-foe__next">They will be back. Each time you beat them, their luck runs thinner.</p>}
    </section>
  );
}

export function EpilogueScreen() {
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const go = useRoute((s) => s.go);
  const g = useGenesis(worldId);
  const opening = useWorldOpening(worldId);
  const world = useWorld(worldId);
  const settlement = useSettlement(opening?.instanceId);
  const offers = useOffers(worldId);
  const heroes = useWorldHeroes(worldId);
  const members = useWorldMembers(worldId);
  const names = useNames(worldId);
  const colours = usePlayerColours(worldId);
  const callings = useHeroCallings(worldId);
  const stageRow = useEncounterStage(opening?.encounterId);
  const clock = useWorldClock(worldId);
  const myOffers = useMyLevelUpOffers().filter((o) => o.worldId === worldId);
  const openLevelUp = useOverlay((s) => s.openLevelUp);
  const seen = useCinematic((s) => s.escapeSeen);
  useCinematic((s) => s.rev);
  const marks = useStoryMarks();
  if (!worldId || !g || !opening) return <main className="fk-intro" data-testid="epilogue-screen" />;
  const keeper = identityHex(world?.owner);
  const fought = heroes.filter((h) => opening.heroes.includes(h.id)).sort((a, b) => (a.id < b.id ? -1 : 1));
  // every player who fought, keeper first, then in the order they joined (the lobby's seats)
  const owners = [...new Set(fought.map((h) => identityHex(h.owner)))];
  const joined = (hex: string) => members.find((m) => identityHex(m.owner) === hex);
  owners.sort((a, b) => (a === keeper ? -1 : b === keeper ? 1 : ((joined(a)?.id ?? 0n) < (joined(b)?.id ?? 0n) ? -1 : 1)));
  const bands: EpilogueBand[] = owners.map((hex) => ({
    key: hex, player: joined(hex)?.displayName || names[hex] || 'A hero', colour: colours.get(hex), you: hex === me, keeper: hex === keeper,
    heroes: fought.filter((h) => identityHex(h.owner) === hex).map((h) => {
      const offer = hex === me ? myOffers.find((o) => o.heroId === h.id) : undefined;
      return { key: h.id.toString(), id: h.id, name: h.name, frame: h.oryxFrame, classId: CLASS_NAMES[h.class] ?? 'fighter', level: h.level, beast: beastOf(callings, h.id), levelUp: offer ? { id: offer.id, toLevel: offer.toLevel } : undefined };
    }),
  }));
  const offer = offers.find((o) => o.id === opening.campaignOffer);
  const cont = () => {
    if (world) { marks.setIntroSeen(world); marks.setEpilogueSeen(world); }
    leaveEncounter();
    go('world', { encounterId: undefined, instanceId: undefined, partyId: undefined });
  };
  return (
    <EpilogueView g={g} won={wonRow(opening.outcomeRow)} bands={bands} field={fieldOf(stageRow)} worldBiome={clock?.biome} worldId={worldId}
      escapeSeen={seen(opening.encounterId)}
      xp={settlement?.xpEach} gold={settlement?.goldEach}
      quest={offer ? { title: offer.title, asker: offer.asker, place: offer.location, pitch: offer.pitch, kind: offer.archetype } : undefined}
      onSheet={(h) => { if (h.id !== undefined) openHeroSheet(h.id); }}
      onGear={(h) => { if (h.id !== undefined) openInventory(h.id); }}
      onLevelUp={openLevelUp}
      onContinue={cont} />
  );
}
